import { ApiProvider } from 'Core'
import useAppView from 'Core/Hooks/useAppView'
import useMultiDispatch from 'Core/Hooks/useMultiDispatch'
import useMultiSlice from 'Core/Hooks/useMultiSlice'
import { enqueueNotification } from 'Utils/Helpers'
import { getAccountIcon } from 'Utils/PureHelperFuctions'
import { LabelContent } from 'V2Components'
import { ErrorLabelContent, getResourceName } from 'features/resources'
import { getHTMLDataTestID } from 'infra/utils'
import _ from 'lodash'
import { Button, Label, SelectDropDown } from 'procyon-ui'
import React, { useState } from 'react'

const ServerResource = ({ rsrc, account }) => {
  const AccountIcon = getAccountIcon(_.get(account, 'Spec.Type', ''.toLowerCase()))
  const [saving, setSaving] = useState('')
  const { selectDispatch } = useMultiDispatch([])
  const { appView } = useAppView()
  const [dbMeshCluster, setDbMeshCluster] = useState((rsrc && rsrc.Spec.MeshCluster) || '')
  const [showRunCheckSave] = useState(rsrc.Spec.PrivateIP !== '127.0.0.1')
  const [isGitServer] = useState(rsrc.Spec.ConnType !== 'Default')

  const getPrivateProxyClusterNames = () => {
    if (!rsrc) return []
    const clusterIDs = [dbMeshCluster]
    const MeshClusterNodes = rsrc.Spec.MeshClusterNodes.ObjectRef
    const existingSelectedClusterId =
      rsrc?.Spec?.MeshCluster !== ''
        ? slices.medusaNodes.find((node) => node.ClusterID === rsrc.Spec.MeshCluster)
        : {}

    const existingSelectedClusterRef = {
      RefKind: existingSelectedClusterId?.ObjectMeta?.Kind,
      RefID: existingSelectedClusterId?.ObjectMeta?.ID
    }

    const combinedClusterNodes = [...MeshClusterNodes]
    combinedClusterNodes.push(existingSelectedClusterRef)
    combinedClusterNodes?.forEach((element) => {
      const matchedNodeID = slices.medusaNodes.filter(
        (meta) => element.RefID === meta.ObjectMeta.ID
      )
      if (matchedNodeID.length > 0) {
        // Extract ClusterID values from matchedNodeID
        const clusterIDsFromMatchedNodes = matchedNodeID.map((node) => node)

        // Push the ClusterID values into clusterIDs
        clusterIDs.push(...clusterIDsFromMatchedNodes)
      }
    })

    const filteredArray = clusterIDs.filter((item) => typeof item === 'object')
    const updatedUniqueArray = []
    const uniqueNode = []
    filteredArray.map((item) => {
      if (uniqueNode.includes(item.ObjectMeta.ID)) {
        return
      } else {
        uniqueNode.push(item.ObjectMeta.ID)
        updatedUniqueArray.push(item)
      }
    })

    return [...updatedUniqueArray]
  }

  /**
   *
   * @param {'Password' | 'Username' | 'MeshCluster'} save
   */
  const handleSpecUpdate = async (save) => {
    try {
      const dataObj = structuredClone(rsrc)
      setSaving(save)
      if (save === 'MeshCluster') dataObj.Spec.MeshCluster = dbMeshCluster
      await new ApiProvider('servers').setInstance(dataObj).put()
      enqueueNotification(`Server ${save} successfully!`, 'info')
      selectDispatch(['databases'])
    } catch (error) {
    } finally {
      setSaving('')
    }
  }

  const handleRunCheck = () => {
    try {
      const dataObj = structuredClone(rsrc)
      dataObj.Spec.MeshCluster = ''
      dataObj.Spec.MeshClusterNodes.ObjectRef = []
      new ApiProvider('servers').setInstance(dataObj).put()
      enqueueNotification(`Run checked successfully!`, 'info')
      selectDispatch(['databases'])
    } catch (error) {
    } finally {
      setSaving('')
    }
  }

  const { slices } = useMultiSlice(['medusaNodes'])

  if (!rsrc) return null

  return (
    <>
      <ErrorLabelContent rsrc={rsrc} />
      <LabelContent
        data-testid={'name'}
        dataTestId={getHTMLDataTestID('server', 'label', 'name')}
        title='Name'
        content={getResourceName(rsrc)}
      />
      {!isGitServer && (
        <LabelContent
          data-testid={'region'}
          dataTestId={getHTMLDataTestID('server', 'label', 'region')}
          title='Region'
          content={rsrc.Spec.Region}
          showEmptyLabel
        />
      )}
      <LabelContent
        data-testid={'dns-name'}
        dataTestId={getHTMLDataTestID('server', 'label', 'dns-name')}
        title='DNS Names'
        content={rsrc.Spec.DNSNames.Elems.join(', ')}
        showEmptyLabel
      />
      {!isGitServer && (
        <LabelContent
          data-testid={'vpc-id'}
          dataTestId={getHTMLDataTestID('server', 'label', 'vpc-id')}
          title='VPC ID'
          content={rsrc.Spec.VpcId}
          showEmptyLabel
        />
      )}

      {rsrc.Spec.PublicIP !== '' && (
        <LabelContent
          data-testid={'public-ip'}
          dataTestId={getHTMLDataTestID('server', 'label', 'public-ip')}
          title='Public IP'
          content={rsrc.Spec.PublicIP}
        />
      )}

      {rsrc.Spec.PrivateIP !== '' && (
        <LabelContent
          data-testid={'private-ip'}
          dataTestId={getHTMLDataTestID('server', 'label', 'private-ip')}
          title='Private IP'
          content={rsrc.Spec.PrivateIP}
        />
      )}
      {!isGitServer && (
        <LabelContent
          data-testid={'account'}
          dataTestId={getHTMLDataTestID('server', 'label', 'account')}
          title='Account'
          content={
            <>
              <AccountIcon className='mr-2' />
              {getResourceName(account)}
            </>
          }
        />
      )}

      {appView === 'admin' && !isGitServer && getPrivateProxyClusterNames().length > 0 ? (
        <LabelContent
          data-testid={'cluster-name'}
          dataTestId={getHTMLDataTestID('server', 'label', 'cluster-name')}
          title='Cluster Name'
          content={
            <div className='flex justify-between items-center'>
              <SelectDropDown
                showHelpText={false}
                className='!min-w-[200px] mr-2'
                menuItems={[
                  ...getPrivateProxyClusterNames().map((e) => ({
                    label: e.ClusterID,
                    value: e.ClusterID
                  }))
                ]}
                onChange={(e) => e.target.value && setDbMeshCluster(e.target.value)}
                value={dbMeshCluster}
              />

              {showRunCheckSave && (
                <div className='flex gap-3'>
                  <Button
                    data-testid={getHTMLDataTestID('server', 'button', 'run-check')}
                    variant='primary'
                    onClick={() => handleRunCheck()}
                  >
                    Run Check
                  </Button>

                  <Button
                    data-testid={getHTMLDataTestID('server', 'button', 'save')}
                    onClick={() => handleSpecUpdate('MeshCluster')}
                    variant='primary'
                  >
                    Save
                  </Button>
                </div>
              )}
            </div>
          }
        />
      ) : dbMeshCluster ? (
        <LabelContent
          data-testid={'mesh-cluster'}
          dataTestId={getHTMLDataTestID('server', 'label', 'mesh-cluster')}
          title='Mesh Cluster'
          content={<Label className='!normal-case' text={dbMeshCluster} />}
        />
      ) : null}
    </>
  )
}

export { ServerResource }
